import { useCallback } from "react"

import type { ChartClickFilter } from "../dashboard/catalog"

import { useDashboardStateStore } from "../state/dashboard-state"

// Recharts' click-event payload shape varies per chart family (Bar
// gets `BarRectangleItem`, Pie gets `PieSectorDataItem`, Line gets
// the bare datum). We accept `unknown` here and look for `payload`
// at runtime — same defensive shape Recharts uses internally. The
// callers cast on the way in via the type-narrowed wrapper.
type RechartsClickPayload = unknown

/**
 * Build a Recharts-compatible click handler that writes the clicked
 * datum's value into the dashboard state at the configured path.
 * Combined with `useAutoRefreshOnFilterChange`, this turns any
 * bar / pie segment / line point into a live filter — the canonical
 * Power-BI cross-filtering UX, in ~30 lines of glue.
 *
 * Returns `undefined` when no filter is configured so callers can
 * spread it onto Recharts without an empty function being attached
 * (Recharts evaluates `onClick` presence to decide whether to flip
 * the cursor — passing a no-op makes the chart misrepresent itself
 * as interactive).
 *
 * `valueKey` defaults to `defaultValueKey` (typically the chart's
 * xKey) — that's the column the click most likely should filter on.
 * Authors can override per chart by setting `valueKey` on the
 * descriptor. We also accept a `Pie`-style {name, value} shape where
 * the value-bearing column is literally "name".
 */
export function useChartClickFilter(
  filter: ChartClickFilter | undefined,
  defaultValueKey?: string
): ((data: RechartsClickPayload) => void) | undefined {
  const store = useDashboardStateStore()
  const handler = useCallback(
    (data: RechartsClickPayload) => {
      if (!filter) return
      // Recharts wraps the original datum in `payload` for bar/line
      // events but passes it raw for pie segments. Accept both shapes
      // so we don't need separate hooks per chart family.
      const obj = data as Record<string, unknown> | null | undefined
      const rawDatum =
        obj && typeof obj === "object" && "payload" in obj
          ? (obj.payload as Record<string, unknown>)
          : obj
      if (!rawDatum || typeof rawDatum !== "object") return
      const key = filter.valueKey ?? defaultValueKey
      if (!key) return
      const value = (rawDatum as Record<string, unknown>)[key]
      if (value === undefined) return
      // Toggle: clicking the SAME value twice clears the filter so
      // the user doesn't have to chase a Clear button on the filter
      // pill. Power BI does the same.
      const current = store.getDashState(filter.stateKey)
      if (current === value) {
        store.setDashState(filter.stateKey, null)
      } else {
        store.setDashState(filter.stateKey, value)
      }
    },
    [filter, defaultValueKey, store]
  )
  return filter ? handler : undefined
}
